import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Eye } from "lucide-react";
import { api } from "../../lib/api";
import { ServiceIcon } from "../../lib/icons";

interface AnfrageRow {
  id: string;
  reference_number: string;
  status: string;
  service_category: string;
  sub_service: string | null;
  urgency: "notfall" | "bald" | "planen";
  customer_vorname: string;
  customer_nachname: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

const COLUMNS: { id: string; label: string; accent: string }[] = [
  { id: "received", label: "Neue Anfragen", accent: "bg-sky-500" },
  { id: "in_review", label: "In Bearbeitung", accent: "bg-amber-500" },
  { id: "inspection_scheduled", label: "Besichtigung", accent: "bg-orange-500" },
  // Fehlte: Karten mit diesem Status verschwanden kommentarlos vom Brett.
  { id: "inspection_done", label: "Besichtigt", accent: "bg-yellow-500" },
  { id: "quote_sent", label: "Angebot gesendet", accent: "bg-violet-500" },
  { id: "quote_declined", label: "Angebot abgelehnt", accent: "bg-rose-500" },
  { id: "quote_approved", label: "Freigegeben", accent: "bg-emerald-500" },
  { id: "scheduled", label: "Eingeplant", accent: "bg-teal-500" },
  { id: "in_progress", label: "In Arbeit", accent: "bg-indigo-500" },
  { id: "completed", label: "Abgeschlossen", accent: "bg-slate-400" },
  // Fehlte ebenfalls - und der Rechnungs-Upload setzt diesen Status automatisch.
  { id: "invoiced", label: "Rechnung gestellt", accent: "bg-slate-500" },
  { id: "paid", label: "Bezahlt", accent: "bg-emerald-600" },
];

const URGENCY: Record<string, { label: string; classes: string }> = {
  notfall: { label: "Notfall", classes: "bg-red-50 text-red-700 ring-1 ring-red-200" },
  bald: { label: "Bald", classes: "bg-orange-50 text-orange-700 ring-1 ring-orange-200" },
  planen: { label: "Flexibel", classes: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" },
};

export function CompanyBoard() {
  const { slug = "" } = useParams();
  const { data, isLoading } = useQuery({
    queryKey: ["company-anfragen", slug],
    queryFn: () =>
      api<{ items: AnfrageRow[] }>(`/master/companies/${slug}/anfragen`),
  });

  const byColumn = useMemo(() => {
    const map = new Map<string, AnfrageRow[]>();
    for (const col of COLUMNS) map.set(col.id, []);
    for (const row of data?.items ?? []) {
      if (map.has(row.status)) map.get(row.status)!.push(row);
    }
    return map;
  }, [data]);

  return (
    <div>
      <Link
        to={`/master/companies/${slug}`}
        className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> {slug}
      </Link>
      <header className="mb-5 flex flex-wrap items-center gap-3">
        <h1 className="page-title">Anfragen</h1>
        <span className="pill bg-amber-50 text-amber-800 ring-1 ring-amber-200">
          <Eye className="h-3 w-3" /> Nur-Lese · Superadmin-Ansicht
        </span>
      </header>

      {isLoading ? (
        <p className="text-slate-500">Lädt…</p>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {COLUMNS.map((col) => (
            <div
              key={col.id}
              className="flex min-h-[60vh] w-[280px] flex-shrink-0 flex-col rounded-2xl border border-slate-200 bg-slate-50/70 p-3"
            >
              <div className="mb-3 flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${col.accent}`} />
                  <h3 className="text-sm font-semibold text-slate-800">
                    {col.label}
                  </h3>
                </div>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-500 ring-1 ring-slate-200">
                  {byColumn.get(col.id)?.length ?? 0}
                </span>
              </div>
              <div className="space-y-2">
                {(byColumn.get(col.id) ?? []).map((a) => {
                  const urg = URGENCY[a.urgency];
                  return (
                    <div
                      key={a.id}
                      className="rounded-xl border border-slate-200 bg-white p-3.5 text-sm shadow-soft"
                    >
                      <div className="mb-1.5 flex items-center justify-between gap-1">
                        <span className="truncate font-semibold text-slate-900">
                          {a.customer_vorname} {a.customer_nachname ?? ""}
                        </span>
                        {urg && (
                          <span className={`pill shrink-0 ${urg.classes}`}>
                            {urg.label}
                          </span>
                        )}
                      </div>
                      <p className="flex items-center gap-1.5 text-xs text-slate-600">
                        <ServiceIcon
                          id={a.service_category}
                          className="h-3.5 w-3.5 text-slate-500"
                        />
                        <span className="truncate">
                          {a.sub_service ?? a.service_category}
                        </span>
                      </p>
                      {a.description && (
                        <p className="mt-1.5 line-clamp-2 text-xs text-slate-500">
                          {a.description}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
